import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Shield,
  User,
  Loader2,
} from 'lucide-react';
import { adminApi } from '../../utils/admin-api';
import { sessionStorage } from '../../utils/auth-api';
import { toast } from 'sonner';

const getDefaultPermissions = () => ({
  energyConsumption: { view: true, download: false },
  manualLogging: { view: true, download: false, edit: true },
  livePlcData: { view: true, download: false },
  scanners: { view: true, download: false },
  waveshare8ch: { view: true },
  reports: { view: true, summaryReport: false },
  offlineData: { view: true, download: false, sync: true },
  scrapManagement: { view: true, download: false, edit: true },
  supportTickets: { view: true, create: true },
  settings: { view: false, edit: false },
  hmi: { view: true },
});

const getFullAccessPermissions = () => ({
  energyConsumption: { view: true, download: true },
  manualLogging: { view: true, download: true, edit: true },
  livePlcData: { view: true, download: true },
  scanners: { view: true, download: true },
  waveshare8ch: { view: true },
  reports: { view: true, summaryReport: true },
  offlineData: { view: true, download: true, sync: true },
  scrapManagement: { view: true, download: true, edit: true },
  supportTickets: { view: true, create: true },
  settings: { view: true, edit: true },
  hmi: { view: true },
});

const normalizePermissions = (permissions: any = {}) => {
  const defaults = getDefaultPermissions();
  return Object.fromEntries(
    Object.entries(defaults).map(([module, moduleDefaults]) => [
      module,
      {
        ...moduleDefaults,
        ...(permissions?.[module] || {}),
      },
    ])
  ) as ReturnType<typeof getDefaultPermissions>;
};

export function UsersTab() {
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [operators, setOperators] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'operator' as 'admin' | 'supervisor' | 'operator',
    permissions: getDefaultPermissions(),
  });

  const session = sessionStorage.get();
  const token = session?.token || '';

  useEffect(() => {
    if (formData.role !== 'admin') return;
    setFormData((prev) => ({
      ...prev,
      permissions: getFullAccessPermissions(),
    }));
  }, [formData.role]);

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    if (selectedClient) {
      loadOperators();
    }
  }, [selectedClient]);

  const loadClients = async () => {
    try {
      const data = await adminApi.getClients(token);
      setClients(data);
      if (data.length > 0) {
        setSelectedClient(data[0].id);
      }
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadOperators = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getUsers(token, selectedClient);
      setOperators(data);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      toast.error('Username and password are required');
      return;
    }

    try {
      const payload: any = {
        email: formData.username,
        name: formData.username,
        role: formData.role,
        permissions: formData.permissions,
      };
      if (formData.password) payload.password = formData.password;

      if (editingUser) {
        await adminApi.updateUser(token, selectedClient, editingUser.id, payload);
        toast.success('User updated successfully');
      } else {
        await adminApi.createUser(token, selectedClient, payload);
        toast.success('User created successfully');
      }
      
      loadOperators();
      handleDialogClose();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleEdit = (user: any) => {
    setEditingUser(user);
    setFormData({
      username: user.email,
      password: '',
      role: user.role === 'admin' ? 'admin' : user.role === 'supervisor' ? 'supervisor' : 'operator',
      permissions: normalizePermissions(user.permissions),
    });
    setDialogOpen(true);
  };

  const handleDelete = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await adminApi.deleteUser(token, selectedClient, userId);
      toast.success('User deleted successfully');
      loadOperators();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setFormData({
      username: '',
      password: '',
      role: 'operator',
      permissions: getDefaultPermissions(),
    });
  };

  const updatePermission = (module: string, permission: string, value: boolean) => {
    setFormData({
      ...formData,
      permissions: {
        ...normalizePermissions(formData.permissions),
        [module]: {
          ...normalizePermissions(formData.permissions)[module as keyof ReturnType<typeof getDefaultPermissions>],
          [permission]: value,
        },
      },
    });
  };

  if (loading && clients.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-xl sm:text-2xl font-bold tracking-tight">User Management</h3>
          <p className="text-sm sm:text-base text-muted-foreground">Manage admin and operator users for each client</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
              <DialogDescription>
                Create a new admin or operator account with custom permissions
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Username *</Label>
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="Enter username"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingUser ? 'Leave blank to keep current' : 'Enter password'}
                  />
                </div>

                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="role">Role</Label>
                  <Select value={formData.role} onValueChange={(value: any) => setFormData({ ...formData, role: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-blue-500" />
                          Admin (Full Access)
                        </div>
                      </SelectItem>
                      <SelectItem value="supervisor">
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-purple-500" />
                          Supervisor (Custom Access)
                        </div>
                      </SelectItem>
                      <SelectItem value="operator">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-green-500" />
                          Operator (Limited Access)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Permissions */}
              <div className="space-y-4">
                <h4 className="font-semibold">Module Permissions</h4>
                {formData.role === 'admin' && (
                  <p className="text-xs text-muted-foreground">
                    Admin always has full access. Use Supervisor/Operator to configure custom screen access and downloads.
                  </p>
                )}
                
                {/* Energy Consumption */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Energy Consumption</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="ec-view">View Access</Label>
                      <Switch
                        id="ec-view"
                        checked={formData.permissions.energyConsumption.view}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('energyConsumption', 'view', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="ec-download">Download PDF</Label>
                      <Switch
                        id="ec-download"
                        checked={formData.permissions.energyConsumption.download}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('energyConsumption', 'download', checked)}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Manual Logging */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Manual Logging</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="ml-view">View Access</Label>
                      <Switch
                        id="ml-view"
                        checked={formData.permissions.manualLogging.view}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('manualLogging', 'view', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="ml-edit">Edit/Add Entries</Label>
                      <Switch
                        id="ml-edit"
                        checked={formData.permissions.manualLogging.edit}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('manualLogging', 'edit', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="ml-download">Download PDF</Label>
                      <Switch
                        id="ml-download"
                        checked={formData.permissions.manualLogging.download}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('manualLogging', 'download', checked)}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Live PLC Data */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Live PLC Data</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="lpd-view">View Access</Label>
                      <Switch
                        id="lpd-view"
                        checked={formData.permissions.livePlcData.view}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('livePlcData', 'view', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="lpd-download">Download PDF</Label>
                      <Switch
                        id="lpd-download"
                        checked={formData.permissions.livePlcData.download}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('livePlcData', 'download', checked)}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Scanners */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Scanners</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="sc-view">View Access</Label>
                      <Switch
                        id="sc-view"
                        checked={formData.permissions.scanners.view}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('scanners', 'view', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="sc-download">Download PDF/Excel</Label>
                      <Switch
                        id="sc-download"
                        checked={formData.permissions.scanners.download}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('scanners', 'download', checked)}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Waveshare 8CH */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Waveshare 8CH</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="waveshare-view">View Access</Label>
                      <Switch
                        id="waveshare-view"
                        checked={formData.permissions.waveshare8ch.view}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('waveshare8ch', 'view', checked)}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Reports */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Reports</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="rep-view">View Access</Label>
                      <Switch
                        id="rep-view"
                        checked={formData.permissions.reports.view}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('reports', 'view', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="rep-summary">Summarise Report Access</Label>
                      <Switch
                        id="rep-summary"
                        checked={formData.permissions.reports.summaryReport}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('reports', 'summaryReport', checked)}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* HMI */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">HMI</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="hmi-view">View Access</Label>
                      <Switch
                        id="hmi-view"
                        checked={formData.permissions.hmi.view}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('hmi', 'view', checked)}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Offline Data */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Offline Data</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="od-view">View Access</Label>
                      <Switch
                        id="od-view"
                        checked={formData.permissions.offlineData.view}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('offlineData', 'view', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="od-sync">Sync</Label>
                      <Switch
                        id="od-sync"
                        checked={formData.permissions.offlineData.sync}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('offlineData', 'sync', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="od-download">Download</Label>
                      <Switch
                        id="od-download"
                        checked={formData.permissions.offlineData.download}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('offlineData', 'download', checked)}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Scrap Management */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Scrap Management</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="sm-view">View Access</Label>
                      <Switch
                        id="sm-view"
                        checked={formData.permissions.scrapManagement.view}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('scrapManagement', 'view', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="sm-edit">Edit/Add Entries</Label>
                      <Switch
                        id="sm-edit"
                        checked={formData.permissions.scrapManagement.edit}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('scrapManagement', 'edit', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="sm-download">Download PDF</Label>
                      <Switch
                        id="sm-download"
                        checked={formData.permissions.scrapManagement.download}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('scrapManagement', 'download', checked)}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Support Tickets */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Support Tickets</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="st-view">View Tickets</Label>
                      <Switch
                        id="st-view"
                        checked={formData.permissions.supportTickets.view}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('supportTickets', 'view', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="st-create">Create Tickets</Label>
                      <Switch
                        id="st-create"
                        checked={formData.permissions.supportTickets.create}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('supportTickets', 'create', checked)}
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Settings */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="s-view">View Access</Label>
                      <Switch
                        id="s-view"
                        checked={formData.permissions.settings.view}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('settings', 'view', checked)}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="s-edit">Edit Settings</Label>
                      <Switch
                        id="s-edit"
                        checked={formData.permissions.settings.edit}
                        disabled={formData.role === 'admin'}
                        onCheckedChange={(checked) => updatePermission('settings', 'edit', checked)}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={handleDialogClose}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingUser ? 'Update User' : 'Create User'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Client Selector */}
      <Card>
        <CardHeader>
          <CardTitle>Select Client</CardTitle>
          <CardDescription>Choose a client to manage their users</CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger>
              <SelectValue placeholder="Select a client" />
            </SelectTrigger>
            <SelectContent>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Users List */}
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>Admin, supervisor and operator accounts</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : operators.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No users created yet. Add your first user above.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {operators.map((user, index) => (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center justify-between p-4 border rounded-lg hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      user.role === 'admin' 
                        ? 'bg-blue-100 dark:bg-blue-900/30' 
                        : user.role === 'supervisor'
                          ? 'bg-purple-100 dark:bg-purple-900/30'
                          : 'bg-green-100 dark:bg-green-900/30'
                    }`}>
                      {user.role === 'admin' ? (
                        <Shield className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      ) : user.role === 'supervisor' ? (
                        <Shield className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                      ) : (
                        <User className="h-6 w-6 text-green-600 dark:text-green-400" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-semibold">{user.email}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant={user.role === 'admin' ? 'default' : user.role === 'supervisor' ? 'outline' : 'secondary'}>
                          {user.role === 'admin' ? 'Admin' : user.role === 'supervisor' ? 'Supervisor' : 'Operator'}
                        </Badge>
                        <p className="text-xs text-muted-foreground">
                          Created {new Date(user.created_at || user.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(user)}>
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(user.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
